import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import TextInput from '../TextInput';
import { CustomerContext } from '../../contexts/CustomerContext';
import styles from './AccessCodeOnlyForm.module.scss';
import useQueryString from '../../hooks/use-query-string';
import { routes } from '../../routes';
import { Student, StudentImage, School } from '../../models';
import AffirmationText from './AffirmationText';
import SchoolInput from '../SchoolInput';
import PictureDayNotice from './PictureDayNotice';
import BeforePictureDay, { formatDate, offerTerms } from './BeforePictureDay';
import SignupThankYou from './SignupThankYou';
import useSchools from '../../hooks/useSchools';
import usePictureDays from '../../hooks/usePictureDays';
import { isValidEmailFormat, suggestEmail } from '../../utils/emailValidation';
import shoobMark from '../../assets/shoob-mark.png';
import { stampEntryMethod } from './stampEntryMethod';

// Legacy export -- some other files import this. Kept as a thin wrapper
// around the shared isValidEmailFormat so we don't have two email regexes
// drifting apart.
export function isValidEmail(email) {
  return isValidEmailFormat(email);
}

// Access codes generated by StudentImage#user_friendly_code are exactly 6
// characters, but the customer's physical sitting card / proof / composite
// prints the code with a source-marker prefix ("S-", "P-", "C-", "M-").
// The fetch path already strips those prefixes; the validator mirrors that
// stripping so `P-H2K7QR` (8 chars raw / 6 chars parsed) counts as valid.
function stripAccessCodePrefix(input) {
  const upper = (input || '').toUpperCase();
  const match = upper.match(/^[SPCM]-(.+)$/);
  return match ? match[1] : upper;
}

function isValidAccessCode(input) {
  return stripAccessCodePrefix(input).length >= 6;
}

// WHERE the shopper came from, for Cart#created_location. A printed-code
// prefix (S- sitting card, P- proof) is the strongest signal; otherwise the
// page URL's `from=` (e.g. the ParentSquare graphic links to
// /c/student_id_entry?from=parentsquare, #5192). Until that param was read
// here every ParentSquare visit stored the `home_page` default. Returns
// undefined when there is nothing to say so an unchanged cart is not re-saved.
export function createdLocationFor(accessCode, from) {
  const upper = (accessCode || '').toUpperCase();
  if (upper.includes('S-')) return 'sitting_cards';
  if (upper.includes('P-')) return 'proofs';
  return from || undefined;
}

// Local, page-scoped strings dictionary. Adding proper app-wide i18n
// (react-i18next) is a separate project; this covers just the entry page
// via a `?lang=es` query param so Spanish-speaking parents can complete
// the code entry step. If i18n gets installed later, retire this dict.
const STRINGS = {
  en: {
    langToggleLabel: 'Español',
    titlePictures: 'Purchase Pictures',
    titleSenior: 'Purchase Senior Pictures',
    subtitle: 'Enter your student’s access code to get started.',
    notFoundTitle: 'We couldn’t find a match.',
    // No "your photos might not be ready yet" hedge. It shipped when that was a
    // plausible explanation; it isn't now. Picture day has demonstrably happened
    // by the time a parent sees THIS message (the pre-picture-day case has its
    // own copy above), and a student's photos go live within
    // ParentReady::Settings::DEADLINE of them being shot. So the hedge sent
    // someone away to wait for photos that were already on the site, when what
    // actually happened is that they mistyped. One line naming the two things
    // worth checking; the "Don't have your code?" link below it covers the
    // parent who hasn't got one at all.
    notFoundBody: 'Double-check your access code and the school you chose.',
    // The generic message above is actively WRONG for a school that hasn't been
    // photographed yet -- it tells a parent to double-check a code that cannot
    // exist and to wait for photos that were never taken.
    //
    // Deliberately does NOT re-explain why: the notice sits a few lines below
    // with the dates and says it there, and printing the same sentence twice on
    // one screen reads as a glitch. This adds only what the notice can't -- the
    // one thing that might actually be wrong, which is the school.
    notFoundPreSeasonBody: (school) => `Pictures at ${school} will be available to order on Picture Day — see the date below. If your access code is from a previous school year, check that you’ve chosen the right school.`,
    // ON PICTURE DAY ITSELF, where "double-check your access code" is a lie
    // roughly half the time. A failed lookup cannot tell the two parents apart:
    // one was photographed this morning and mistyped, the other is looking
    // before their child has sat, and for them there is genuinely nothing on
    // the site yet. So this serves both -- the timing first, because that is
    // the parent the generic copy insults, then the mistype in one clause.
    //
    // "Shortly" rather than a number of minutes: photos land within
    // ParentReady::Settings::DEADLINE of a student sitting, but that is our
    // internal target, not a promise a parent should be timing us against.
    //
    // SPLIT INTO LEAD + BODY, and the lead REPLACES "We couldn't find a match."
    // rather than sitting under it. On picture day the parent has most likely
    // done nothing wrong -- they are early -- so leading with a failure and
    // then explaining it away still lands the accusation first. The lead takes
    // the title slot so the bold-lead/body hierarchy the banner is designed
    // around survives; only the wording changes.
    //
    // TWO SENTENCES, AND NO MORE. The first draft ran to four and read as a
    // wall of red text that a parent scanning the page would skip -- which
    // defeats the whole point of writing a true message instead of a generic
    // one. What was cut: "if your child was already photographed today,
    // double-check your access code and the school you chose." That parent is
    // already served by the "Look up your code" link below and by the code
    // field they just typed into, so the sentence bought nothing and cost the
    // length that stops the message being read at all.
    notFoundPictureDayLead: (school) => `Pictures at ${school} are being taken today.`,
    notFoundPictureDayBody: 'Photos go online shortly after each student is photographed — we’ll text and email you a link.',
    notFoundLinkPrefix: 'Don’t have your code? ',
    notFoundLink: 'Look up your code →',
    schoolLabel: 'School',
    schoolPlaceholder: 'Type your school name',
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    accessCodeLabel: 'Access code',
    accessCodePlaceholder: 'e.g. H2K7QR',
    // The SAME offer the pre-picture-day screen quotes, in the tense that
    // suits photos which already exist. Never rendered without a deadline the
    // coupon actually gave -- see PictureDayOffer.
    orderBy: (terms, date) => `Order by ${date} to receive ${terms}.`,
    percent: (n) => `${n}% off`,
    amount: (n) => `$${n} off`,
    freeShipping: 'free shipping',
    and: ' and ',
    whereIsCode: 'Where do I find my Access Code?',
    hideHelp: 'Hide help',
    helperText: (example) => (
      // Rules pulled from StudentImage#user_friendly_code (app/models/
      // student_image.rb): 6 uppercase alphanumeric chars, deliberately
      // excluding O / 0 / I / 1 / L to avoid printed-character confusion.
      // `translate="no"` keeps the product-specific card name and the
      // code example intact when a parent uses their browser's built-in
      // Translate feature.
      // eslint-disable-next-line max-len, react/jsx-one-expression-per-line
      <>Your access code is printed on the <span translate="no">sitting card</span> or proof sheet sent home with students. It’s 6 characters — a mix of letters and numbers (for example <span translate="no">{example}</span>).</>
    ),
    required: '* Required',
    continue: 'Continue',
    noCodePrefix: 'Don’t have an access code? ',
    noCodeLink: 'Look up your code →',
    privacyTitle: 'Your Privacy Matters',
    privacyBody: 'Your photos are stored securely and only accessible with your unique code.',
    emailInvalid: 'That doesn’t look like a complete email address.',
    emailSuggestionPrefix: 'Did you mean ',
    emailSuggestionSuffix: '?',
    accessCodeInvalid: 'Access code needs to be at least 6 characters.',
  },
  es: {
    langToggleLabel: 'English',
    titlePictures: 'Comprar fotos',
    titleSenior: 'Comprar fotos de graduación',
    subtitle: 'Ingrese el código de acceso de su estudiante para comenzar.',
    notFoundTitle: 'No pudimos encontrar una coincidencia.',
    notFoundBody: 'Verifique su código de acceso y la escuela que eligió.',
    notFoundPreSeasonBody: (school) => `Las fotos de ${school} se pueden ordenar después del día de fotos — vea las fechas a continuación. Si su código de acceso es de un año escolar anterior, verifique que haya elegido la escuela correcta.`,
    // "Se están tomando" (present progressive) -- the shoot is happening as
    // they read this, which is the whole point of the message.
    notFoundPictureDayLead: (school) => `Las fotos de ${school} se están tomando hoy.`,
    notFoundPictureDayBody: 'Las fotos estarán disponibles poco después de que se fotografíe a cada estudiante — le enviaremos un mensaje de texto y un correo electrónico con un enlace.',
    notFoundLinkPrefix: '¿No tiene su código? ',
    notFoundLink: 'Busque su código →',
    schoolLabel: 'Escuela',
    schoolPlaceholder: 'Escriba el nombre de su escuela',
    emailLabel: 'Correo electrónico',
    emailPlaceholder: 'usted@ejemplo.com',
    accessCodeLabel: 'Código de acceso',
    accessCodePlaceholder: 'ej. H2K7QR',
    orderBy: (terms, date) => `Ordene antes del ${date} para recibir ${terms}.`,
    percent: (n) => `${n}% de descuento`,
    amount: (n) => `$${n} de descuento`,
    freeShipping: 'envío gratis',
    and: ' y ',
    whereIsCode: '¿Dónde encuentro mi código de acceso?',
    hideHelp: 'Ocultar ayuda',
    helperText: (example) => (
      // eslint-disable-next-line max-len, react/jsx-one-expression-per-line
      <>Su código de acceso está impreso en la <span translate="no">sitting card</span> u hoja de pruebas enviada a casa con los estudiantes. Tiene 6 caracteres — una combinación de letras y números (por ejemplo <span translate="no">{example}</span>).</>
    ),
    required: '* Requerido',
    continue: 'Continuar',
    noCodePrefix: '¿No tiene un código de acceso? ',
    noCodeLink: 'Busque su código →',
    privacyTitle: 'Su privacidad es importante',
    privacyBody: 'Sus fotos se almacenan de forma segura y solo son accesibles con su código único.',
    emailInvalid: 'Eso no parece una dirección de correo electrónico completa.',
    emailSuggestionPrefix: '¿Quiso decir ',
    emailSuggestionSuffix: '?',
    accessCodeInvalid: 'El código de acceso debe tener al menos 6 caracteres.',
  },
};

export default function AccessCodeOnlyForm() {
  const [accessCode, setAccessCode] = useState('');
  const { customer, setCustomer } = useContext(CustomerContext);
  const { order, lang, from } = useQueryString();
  const location = useLocation();
  const t = STRINGS[lang === 'es' ? 'es' : 'en'];
  // Defined up here (not just before the form's return) because the
  // affirmation screen returns early and needs it too.
  const currentLang = lang === 'es' ? 'es' : 'en';
  const otherLang = lang === 'es' ? 'en' : 'es';
  // Preserve everything already in the URL when toggling language.
  const langToggleSearch = (() => {
    const params = new URLSearchParams(location.search);
    params.set('lang', otherLang);
    return `?${params.toString()}`;
  })();
  const [student, setStudent] = useState();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [packageId, setPackageId] = useState();
  const [studentImageSeason, setStudentImageSeason] = useState('');
  const history = useHistory();
  const schools = useSchools();
  const [email, setEmail] = useState(customer.email || '');
  const [emailError, setEmailError] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  // Same as the look-up page: on blur, flag a structurally-invalid email
  // and/or a "did you mean X?" suggestion for common typos. Both hints
  // sit under the email input; format-invalid wins if both apply.
  const [emailFormatBad, setEmailFormatBad] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  // Inline "code is too short" hint. Set on blur (so we don't yell mid-type)
  // and on submit; cleared as the user keeps typing.
  const [accessCodeInvalid, setAccessCodeInvalid] = useState(false);
  // Required-field validation stays hidden until the user tries to submit
  // with something missing -- then a red "Required" hint + red field
  // border appears next to each empty field, and clears as they fill it in.
  const [showRequired, setShowRequired] = useState(false);
  // Track school selection LOCALLY (null until handleSchoolChange fires).
  // customer.currentSchool comes from context and can be non-null from a
  // prior visit even when this form's SchoolInput is visibly empty; trusting
  // it as "the user picked a school on this page" let a stale value slip
  // through and the API fired without a real selection, producing the
  // "not found" banner instead of a "school required" hint.
  const [pickedSchool, setPickedSchool] = useState(null);
  // True while pickedSchool holds only the id+name stand-in taken from the
  // dropdown option, i.e. the pick landed before the school list resolved.
  const [needsFullSchool, setNeedsFullSchool] = useState(false);
  // Upcoming picture/retake days for the chosen school. Drives the notice that
  // tells a parent arriving before picture day why there is nothing to order
  // yet -- the single biggest source of confused traffic on this page, because
  // the rest of the industry sells pre-orders and we don't.
  // SENIORS DON'T GET THE PICTURE-DAY NOTICE. They reach this same component
  // from "Order Senior Portraits" in the nav, and senior portraits are booked
  // as appointments rather than shot on a school picture day -- which is why
  // SENR is excluded from SchoolPictureDays::PORTRAIT_TYPES in the first place.
  // Left alone, the card would answer a senior parent with the school's
  // UNDERGRAD date: "available to order on Picture Day, Monday August 31" for a
  // sitting that happened months ago and may already be on sale. Four schools
  // currently have senior work and an upcoming picture day, so it is a small
  // population, but it is the wrong answer to the question they asked.
  //
  // Skipping the id also skips the request, so the senior flow doesn't pay for
  // a lookup it will never show.
  const isSeniorOrder = order === 'senior';
  const { pictureDays, loading: loadingPictureDays } = usePictureDays(
    isSeniorOrder ? null : pickedSchool?.id,
  );

  const preSeason = Boolean(pictureDays && !pictureDays.seasonStarted && pictureDays.days?.length);

  // Picture day is TODAY, which changes only ONE thing: what a failed code
  // lookup says. The fields stay, because the family photographed this morning
  // can genuinely order -- see the gate below.
  //
  // Straight from the server; do NOT rederive it by comparing fallPictureDay to
  // the browser's clock (SchoolPictureDays#fall_picture_day_today? says why).
  //
  // `Boolean(pickedSchool) &&` for consistency with beforePictureDay below,
  // which carries it as a genuine crash guard. Here the same stale-data window
  // is only cosmetic and may not be reachable at all: pictureDays is cleared in
  // an effect, so in principle this could read the previous school's `true` on
  // the render where pickedSchool has just become null, and the lead would
  // interpolate an undefined name -- "Pictures at undefined are being taken
  // today." In practice React flushes the effect before paint, and it could not
  // be reproduced in a test (act() flushes effects before anything is
  // observable), so treat this as belt-and-braces rather than a fixed bug.
  // Raised by review on PR #5255; guarded because the asymmetry between two
  // adjacent expressions is itself the hazard.
  const pictureDayToday = Boolean(pickedSchool) && pictureDays?.fallPictureDayToday === true;

  // TWO DISTINCT SCREENS, decided by whether THE fall picture day (E001/J001/
  // U001) has happened.
  //
  // `=== false` deliberately, not `!`: fallPictureDayPast is three-state, and
  // null means this school has no picture day on the calendar at all -- a real
  // condition for one signed after the schedule was built. Those keep the
  // existing screen rather than being shown a date that does not exist.
  //
  // `false` means STRICTLY BEFORE picture day, because the server side is
  // inclusive of today -- see SchoolPictureDays#fall_picture_day_past?. It was
  // exclusive once, which left this screen up all day on picture day itself
  // with no access-code fields anywhere on it while the notice above had
  // already retired itself. If you ever find this screen and `seasonStarted`
  // disagreeing about a school, that is the bug, not a subtlety.
  //
  // Before the day, there are NO access-code fields. Nearly everyone here is a
  // current-season family wanting this season's pictures, many of them
  // conditioned by other companies to prepay online before picture day. We do
  // not sell pre-orders, so the job is to say what will happen and take their
  // contact details. The parent holding a code from an earlier shoot is a very
  // small minority and is served by the post-picture-day screen, not this one.
  //
  // `Boolean(pickedSchool) &&` is a CRASH GUARD, not tidiness, and it matches
  // showCodeFields below for the same reason. usePictureDays clears its data
  // when the school is deselected, but it does so in an EFFECT -- effects run
  // after render, so on the one render where pickedSchool has just become null
  // the previous school's payload is still here. The card reads
  // `pickedSchool.id` (it needs a school to sign the parent up to), so a parent
  // who picked a pre-picture-day school and then cleared the School field took
  // the whole page down with a null dereference.
  //
  // `!isSeniorOrder` here as well as at the two render sites below, so the
  // senior exclusion lives in ONE expression. It was enforced only by passing
  // null to usePictureDays, twenty lines up and in a different shape -- which
  // left showCodeFields (which does NOT check isSeniorOrder) hiding the code
  // fields from a senior parent for as long as anything ever put data in
  // `pictureDays` on that flow. Nothing does today; this is why it stays that
  // way.
  const beforePictureDay = Boolean(pickedSchool)
    && !isSeniorOrder
    && pictureDays?.fallPictureDayPast === false;

  // Once a parent has signed up, the whole card becomes a thank-you. Reset by
  // "sign up another student", which a parent with two children at the same
  // school needs and would otherwise have no route back to.
  const [signedUp, setSignedUp] = useState(false);

  // THE PAGE ASKS FOR THE SCHOOL FIRST, because it is the only field a parent
  // can always answer, and the answer changes what the rest of the page should
  // say. Roughly two in five customer schools have not been photographed yet at
  // the start of the season, and this page's untracked traffic skews further
  // that way still -- the families whose photos ARE ready arrive by clicking the
  // link we emailed them, so the people who type this URL in are
  // disproportionately the ones with nothing to order.
  //
  // But once a school IS chosen the code fields always appear, pre-season or
  // not. They were briefly kept behind a button for pre-season schools, and
  // that button was the dead end in miniature: a parent holding a code from an
  // earlier shoot had to recognise themselves in a one-line prompt before the
  // page would show them anywhere to type it. The notice above them explains
  // the situation; it does not need to gate them too.
  //
  // Still held back while the lookup is in flight, so the notice can't appear
  // above a field the parent has already started typing in and shove it down
  // the page. A failed lookup clears `loading` and lets them through.
  const showCodeFields = Boolean(pickedSchool) && !loadingPictureDays && !beforePictureDay;

  const missingAccessCode = showRequired && !accessCode;
  const missingSchool = showRequired && !pickedSchool;
  const missingEmail = showRequired && !email;

  const isYearbookOrder = order === 'yearbook_order';

  const fetchStudentData = (stuAccessCode, schoolId) => {
    const createdLocation = createdLocationFor(stuAccessCode, from);
    let parsedAccessCode;
    let isMultipleSeasonPackage;

    if (stuAccessCode.toUpperCase().includes('S-')) {
      parsedAccessCode = stuAccessCode.toUpperCase().split('S-')[1];
    } else if (stuAccessCode.toUpperCase().includes('P-')) {
      parsedAccessCode = stuAccessCode.toUpperCase().split('P-')[1];
    } else if (stuAccessCode.toUpperCase().includes('M-')) {
      isMultipleSeasonPackage = true;
      parsedAccessCode = stuAccessCode.toUpperCase().split('M-')[1];
    } else {
      parsedAccessCode = stuAccessCode;
    }

    // Whatever the prefix said about WHERE the code was printed, the shopper
    // TYPED it -- that's the entry method (a scanned QR never hits this form).
    //
    // Extracted so it can be tested: this line threw
    // `customer.cart.isDirty is not a function` for any visitor whose cart came
    // back from localStorage, taking the Continue button with it. See
    // stampEntryMethod.
    stampEntryMethod(customer.cart, { createdLocation });

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);
    const selectExtras = ['packageId', isMultipleSeasonPackage && 'multiSeasonPackageId'].filter((n) => n);

    StudentImage
      .selectExtra(selectExtras)
      .includes('student')
      .where({
        forAccessCodeAndSchool: [parsedAccessCode, schoolId],
        hasImagePresent: schoolId,
      })
      .merge({ student: studentScope })
      .all()
      .then((res) => {
        if (res.data.length === 0) return;

        const studentImage = res.data[0];

        if (isMultipleSeasonPackage && studentImage.multiSeasonPackageId) {
          setPackageId(studentImage.multiSeasonPackageId);
        } else {
          setPackageId(studentImage.packageId);
        }
        setStudent(studentImage.student);
        setStudentImageSeason(studentImage.folder);
        localStorage.setItem(`currentSeason-${studentImage.student.id}`, studentImage.folder);
      })
      .catch((error) => {
        console.error('Error fetching student data:', error);
      })
      .finally(() => {
        setHasSubmitted(true);
        setEmailError(false);
      });
  };

  // The option handed back by SchoolInput already carries the id and name --
  // everything this form needs to validate and run the lookup. Record it
  // immediately and only THEN try to upgrade to the full School record.
  //
  // Looking the option up in `schools` and storing the result directly used to
  // drop the selection outright: SchoolInput runs its own useSchools() fetch,
  // so its list can be populated (and accept a pick) while this component's is
  // still empty. find() returned undefined, pickedSchool stayed null, and the
  // form reported "School * Required" while react-select went on displaying
  // the chosen name from its own internal state.
  const handleSchoolChange = (opt) => {
    if (!opt) {
      setPickedSchool(null);
      setCustomer((c) => ({ ...c, currentSchool: null }));
      return;
    }
    // String() on both sides: ids arrive as strings from the JSON:API models
    // but as numbers from some callers, and === across the two silently misses.
    const full = schools.find((school) => String(school.id) === String(opt.value));
    const newSchool = full || { id: opt.value, name: opt.label };
    setNeedsFullSchool(!full);
    setPickedSchool(newSchool);
    setCustomer((c) => ({ ...c, currentSchool: newSchool }));
  };

  // Swap the id+name stand-in for the real School record once the list lands.
  // Downstream screens read accessCodeOnly / schoolTypeId off
  // customer.currentSchool, which the stand-in doesn't carry.
  //
  // Gated on the needsFullSchool flag rather than on comparing the found
  // record against the stored one. An identity comparison would rely on
  // useSchools handing back a referentially stable array; the moment it
  // doesn't, find() yields a new object every render, the comparison never
  // settles and the effect re-sets state forever (it hung the test suite
  // exactly once). The flag runs this once per pick either way.
  useEffect(() => {
    if (!needsFullSchool || !pickedSchool || schools.length === 0) return;
    const full = schools.find((school) => String(school.id) === String(pickedSchool.id));
    if (!full) return;
    setPickedSchool(full);
    setCustomer((c) => ({ ...c, currentSchool: full }));
    setNeedsFullSchool(false);
    // Deps are exhaustive (no disable comment needed): setCustomer is used in
    // its functional form, so this never closes over `customer`. Re-running
    // when pickedSchool's identity changes is harmless -- the flag is false by
    // then and the effect returns immediately.
  }, [schools, needsFullSchool, pickedSchool]);

  // Clear the "not-found" banner whenever ANY of the three inputs change --
  // otherwise a stale banner from a previous submit lingers while the user is
  // still fixing their entry. The banner is meant strictly for "you filled
  // everything in and we still couldn't find the student."
  useEffect(() => {
    setHasSubmitted(false);
  }, [accessCode, email, pickedSchool?.id]);

  // Force a clean-slate school selection on this page. Without this, a
  // school stored in CustomerContext from a previous visit would silently
  // apply on submit even though the SchoolInput visibly shows the
  // placeholder -- users would think they never picked a school.
  useEffect(() => {
    if (customer?.currentSchool) {
      setCustomer({ ...customer, currentSchool: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = (e) => {
    e.preventDefault();

    setCustomer({ ...customer, currentStudent: student });
    localStorage.setItem('isUsingAccessCode', 'true');

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: student.studentId, sch: customer.currentSchool.id }));
    } else if (!packageId) {
      history.push('/past_images');
    } else {
      history.push(routes.packageSelection({ pid: packageId, senior: studentImageSeason.toLowerCase().includes('senior') }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // While the form is only asking for a school there is nothing to look up,
    // and no submit button either -- but the School combobox lives inside the
    // <form>, so an Enter keypress still lands here. Without this it would
    // light up "Required" on two fields the parent cannot see.
    if (!showCodeFields) return;

    // Flip the required-visibility flag before short-circuiting so ALL missing
    // fields light up at once (rather than making the user submit three times
    // to see each error). Also clear any stale "not-found" banner from a
    // previous submit -- the banner is only for the case where the user has
    // filled everything in and we still can't find them. Uses pickedSchool
    // (local) instead of customer.currentSchool so a context value carried
    // over from a prior visit can't slip through as "school selected."
    if (!accessCode || !pickedSchool || !email) {
      setShowRequired(true);
      setHasSubmitted(false);
      return;
    }
    setShowRequired(false);

    if (!isValidEmailFormat(email)) {
      setEmailError(true);
      setEmailFormatBad(true);
      return;
    }
    if (!isValidAccessCode(accessCode)) {
      setAccessCodeInvalid(true);
      return;
    }

    setCustomer({ ...customer, email });

    const studentScope = Student.selectExtra(['availableSeasonsToPurchase']);

    if (accessCode.toUpperCase().includes('C-')) {
      const parsedAccessCode = accessCode.toUpperCase().split('C-')[1];
      StudentImage
        .selectExtra(['packageId'])
        .includes('student')
        .where({ forAccessCodeAndSchool: [parsedAccessCode, pickedSchool.id] })
        .merge({ student: studentScope })
        .all()
        .then((res) => {
          if (res.data.length === 0) return;

          history.push(`/landing/composites?sid=${res.data[0].student.studentId}&sch=${pickedSchool.id}&access_code=${parsedAccessCode}`);
        })
        .finally(() => {
          setHasSubmitted(true);
          setEmailError(false);
        });
    } else {
      fetchStudentData(accessCode, pickedSchool.id);
    }
  };

  if (student && hasSubmitted) {
    return (
      <div className={styles.page} lang={currentLang}>
        <div className={styles.card}>
          <Link
            to={{ pathname: location.pathname, search: langToggleSearch }}
            className={styles.langToggle}
            translate="no"
            hrefLang={otherLang}
          >
            {t.langToggleLabel}
          </Link>
          <AffirmationText
            handleContinue={handleContinue}
            // Back to the entry form with school / email / code still filled
            // in, for the parent who reads the name and realises the code
            // they typed wasn't their student's.
            handleBack={() => {
              setStudent(undefined);
              setHasSubmitted(false);
            }}
            student={student}
            lang={currentLang}
          />
        </div>
      </div>
    );
  }

  // Once a parent has signed up, the card IS the confirmation -- an early
  // return rather than a branch inside the form, because there is nothing left
  // of the form worth rendering. The privacy note stays: it is about the page,
  // not the form.
  if (signedUp) {
    return (
      <div className={styles.page} lang={currentLang}>
        <div className={styles.card}>
          <SignupThankYou lang={currentLang} onAnother={() => setSignedUp(false)} />
        </div>

        <div className={styles.privacyCallout}>
          <div className={styles.privacyTitle}>{t.privacyTitle}</div>
          <div className={styles.privacyBody}>{t.privacyBody}</div>
        </div>
      </div>
    );
  }

  // Composed with the SAME helpers the pre-picture-day screen uses, so the two
  // cannot drift on how a coupon is worded or how a date is read.
  const offer = pictureDays?.offer;
  const offerTermsText = offer ? offerTerms(offer, t) : '';
  const offerLine = offer && offerTermsText
    ? t.orderBy(offerTermsText, formatDate(offer.expiresOn, currentLang))
    : null;

  const title = order === 'senior' ? t.titleSenior : t.titlePictures;

  return (
    // `lang` on the subtree tells Chrome/Safari/Edge what language the
    // content is in, which drives their "Translate this page?" offer and
    // improves translation quality vs. relying on auto-detection.
    <div className={styles.page} lang={currentLang}>
      <div className={styles.card}>
        <Link
          to={{ pathname: location.pathname, search: langToggleSearch }}
          className={styles.langToggle}
          // The toggle label is written in the OTHER language on purpose
          // (so an English viewer sees "Español" and vice-versa); marking
          // it translate="no" keeps the browser from re-translating it back.
          translate="no"
          hrefLang={otherLang}
        >
          {t.langToggleLabel}
        </Link>
        {/* Decorative: the brand mark repeats what the site header already
            shows, so it is hidden from screen readers rather than announced
            a second time before the page title. */}
        <div className={styles.logoBadge} aria-hidden="true">
          <img src={shoobMark} alt="" className={styles.logoMark} />
        </div>
        <h1 className={`${styles.title} ${showCodeFields ? '' : styles.titleAlone}`}>{title}</h1>
        {/* Only once there IS an access-code field to explain. Before a school
            is chosen the screen is a titled card over a single labelled School
            box, and a line telling the parent to choose their school said
            nothing the form was not already saying. */}
        {showCodeFields && (
          <p className={styles.subtitle}>{t.subtitle}</p>
        )}

        {/* `role="status"` on picture day, not "alert": a screen reader should
            announce it politely rather than interrupt, because nothing has gone
            wrong -- the parent is early. Same reason it loses the red (see
            .infoBanner). */}
        {hasSubmitted && !student && (
          <div
            className={`${styles.errorBanner} ${pictureDayToday ? styles.infoBanner : ''}`}
            role={pictureDayToday ? 'status' : 'alert'}
          >
            <span className={styles.errorIcon} aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </span>
            <div className={styles.errorText}>
              {/* On picture day the lead is the DAY, not the failure. See
                  notFoundPictureDayLead: telling a parent who is merely early
                  that we couldn't find a match lands the accusation first even
                  when the sentence after it explains they've done nothing
                  wrong. */}
              <div className={styles.errorTitle}>
                {pictureDayToday
                  ? t.notFoundPictureDayLead(pickedSchool?.name)
                  : t.notFoundTitle}
              </div>
              {/* THREE ANSWERS, because "we couldn't find you" has three
                  different reasons and only one of them is the parent's fault.
                  Ordered by how wrong the generic copy is for each: before
                  picture day the code cannot exist; ON picture day it may not
                  exist YET; after it, a mistype is the likely explanation. */}
              <div className={styles.errorBody}>
                {preSeason && t.notFoundPreSeasonBody(pickedSchool?.name)}
                {!preSeason && pictureDayToday && t.notFoundPictureDayBody}
                {!preSeason && !pictureDayToday && t.notFoundBody}
              </div>
              {/* "Don't have your code?" ONLY WHERE THAT QUESTION IS STILL
                  OPEN, which is neither of the picture-day states.
                  Everyone who reaches this banner submitted a code -- handleSubmit
                  returns early without one -- so asking whether they have one is
                  answering a question they just answered. It survives on the
                  ordinary failure because there the likely fault IS the code and
                  the right one is worth fetching.
                  Before picture day, and on it, the lookup cannot help anyway:
                  Student#latest_access_code filters through with_shoob_photo, so
                  for a child who has not sat there is nothing to mail and the
                  parent gets "check your email" followed by silence. The
                  standing link BELOW the form is a different case and stays --
                  see the note there. */}
              {!preSeason && !pictureDayToday && (
                <div className={styles.errorAction}>
                  {t.notFoundLinkPrefix}
                  {/* Carry the language across: without it a parent reading this
                      page in Spanish landed on an English lookup form. */}
                  <Link to={routes.getAccessCode({ lang: currentLang })}>{t.notFoundLink}</Link>
                </div>
              )}
            </div>
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={`${styles.field} ${missingSchool ? styles.errorField : ''}`}>
            <label htmlFor="school-input">
              {t.schoolLabel}
              {missingSchool && (
                <span className={styles.required} aria-hidden="true">{t.required}</span>
              )}
            </label>
            <SchoolInput
              onChange={handleSchoolChange}
              hideLabel
              noDefault
              openOnTypeOnly
              placeholder={t.schoolPlaceholder}
              error={missingSchool}
            />
          </div>

          {/* Directly under the School field, so the connection between the
              school just picked and the dates shown is obvious. It sits ABOVE
              the email and access-code fields and never blocks them -- before
              picture day it also carries the line telling a parent that a code
              from an earlier shoot still works in the box just below. */}
          {!isSeniorOrder && beforePictureDay && (
            <BeforePictureDay
              pictureDays={pictureDays}
              schoolId={pickedSchool.id}
              lang={currentLang}
              /* ONE ENDING, always the thank-you. This used to withhold it when
                 the server reported two children by that name, so the card could
                 stay and ask which one -- which meant a namesake's parent got a
                 different, longer ending than everybody else, arriving after they
                 believed they were done.
                 The signup form now settles that at the name fields: it asks the
                 server as the parent types and makes the student ID required for
                 that one parent. So by the time this fires the question is
                 already answered, and withholding the thank-you would only
                 strand them on a form they have finished with. */
              onSignedUp={() => setSignedUp(true)}
            />
          )}

          {!isSeniorOrder && !beforePictureDay && (
            <PictureDayNotice pictureDays={pictureDays} lang={currentLang} />
          )}

          {showCodeFields && (
          <>
            <div className={`${styles.field} ${missingEmail ? styles.errorField : ''}`}>
              <label htmlFor="email">
                {t.emailLabel}
                {missingEmail && (
                <span className={styles.required} aria-hidden="true">{t.required}</span>
                )}
              </label>
              <div className={styles.inputWithIcon}>
                <span className={styles.leadingIcon} aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <TextInput
                  id="email"
                  val={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(false);
                    if (emailFormatBad) setEmailFormatBad(false);
                    if (emailSuggestion) setEmailSuggestion(null);
                  }}
                  onBlur={() => {
                    if (!email) return;
                    setEmailFormatBad(!isValidEmailFormat(email));
                    setEmailSuggestion(suggestEmail(email));
                  }}
                  type="email"
                  autoComplete="email"
                  placeholder={t.emailPlaceholder}
                  aria-required="true"
                  aria-invalid={(missingEmail || emailError || emailFormatBad) || undefined}
                />
              </div>
              {emailFormatBad || (emailError && !missingEmail) ? (
                <div className={styles.errorHint}>{t.emailInvalid}</div>
              ) : emailSuggestion ? (
                <div className={styles.emailSuggestion}>
                  {t.emailSuggestionPrefix}
                  <button
                    type="button"
                    className={styles.emailSuggestionAction}
                    onClick={() => { setEmail(emailSuggestion); setEmailSuggestion(null); }}
                  >
                    {emailSuggestion}
                  </button>
                  {t.emailSuggestionSuffix}
                </div>
              ) : null}
            </div>

            <div className={`${styles.field} ${(missingAccessCode || accessCodeInvalid) ? styles.errorField : ''}`}>
              <div className={styles.labelRow}>
                <label htmlFor="access-code">
                  {t.accessCodeLabel}
                  {missingAccessCode && (
                  <span className={styles.required} aria-hidden="true">{t.required}</span>
                  )}
                </label>
                <button
                  type="button"
                  className={styles.helperToggle}
                  onClick={() => setShowHelper((v) => !v)}
                  aria-expanded={showHelper}
                >
                  <span className={styles.helperIcon} aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </span>
                  {showHelper ? t.hideHelp : t.whereIsCode}
                </button>
              </div>
              <div className={styles.inputWithIcon}>
                <span className={styles.leadingIcon} aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="4" y="11" width="16" height="10" rx="2" />
                    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                  </svg>
                </span>
                <TextInput
                  id="access-code"
                  val={accessCode}
                  onChange={(e) => {
                    setAccessCode(e.target.value);
                    if (accessCodeInvalid) setAccessCodeInvalid(false);
                  }}
                  onBlur={() => {
                    if (!accessCode) return;
                    setAccessCodeInvalid(!isValidAccessCode(accessCode));
                  }}
                  placeholder={t.accessCodePlaceholder}
                  autoComplete="off"
                  autoCapitalize="characters"
                  aria-required="true"
                  aria-invalid={(missingAccessCode || accessCodeInvalid) || undefined}
                />
              </div>
              {accessCodeInvalid && !missingAccessCode && (
              <div className={styles.errorHint}>{t.accessCodeInvalid}</div>
              )}
              {showHelper && (
              <div className={styles.helperPanel}>
                {t.helperText(<code>H2K7QR</code>)}
              </div>
              )}
            </div>

            {/* The SAME offer the pre-picture-day screen quotes, in the tense
                that suits photos which already exist. Directly above the
                button, because it is the reason to press it.

                PictureDayOffer returns null once the deadline has passed, so
                this cannot show a date that is already behind the parent. */}
            {offerLine && (
              <p className={styles.offerLine}>{offerLine}</p>
            )}

            <button type="submit" className={styles.cta}>
              {t.continue}
            </button>
          </>
          )}
        </form>

        {/* Goes with the access-code field, not the page: offering to look up a
            code is noise next to a form that is only asking which school. And
            before picture day it is worse than noise -- the look-up page
            searches the current season, so it can only send the parent to the
            same dead end one screen further along. Suppressed there for the
            same reason the error banner drops its copy of this link. */}
        {showCodeFields && !preSeason && (
          <p className={styles.secondaryRow}>
            {t.noCodePrefix}
            <Link to={routes.getAccessCode({ lang: currentLang })}>{t.noCodeLink}</Link>
          </p>
        )}
      </div>

      <div className={styles.privacyCallout}>
        <div className={styles.privacyTitle}>{t.privacyTitle}</div>
        <div className={styles.privacyBody}>{t.privacyBody}</div>
      </div>
    </div>
  );
}
